import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Cell,
  PieChart as RechartsPieChart,
  Pie
} from 'recharts';
import { exportComparisonToCSV } from '../../utils/tourismExportUtils';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
  },
}));

const ComparisonCard = styled(Card)(({ theme, isHighest, isLowest }) => ({
  border: isHighest ? `2px solid ${theme.palette.error.main}` :
          isLowest ? `2px solid ${theme.palette.success.main}` :
          `1px solid ${theme.palette.divider}`,
  backgroundColor: isHighest ? theme.palette.error.light :
                  isLowest ? theme.palette.success.light :
                  theme.palette.background.paper,
  transition: 'all 0.3s ease',
}));

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00'];

// Helper function - component dışında tanımla
const getCategoryDisplayName = (key) => {
  const names = {
    accommodation: 'Konaklama',
    energy: 'Enerji',
    water: 'Su',
    food: 'Yemek & İçecek',
    laundry: 'Çamaşır',
    waste: 'Atık',
    transportation: 'Ulaşım',
    facilities: 'Tesis Hizmetleri'
  };
  return names[key] || key;
};

const TourismComparisonView = ({ calculations, onRemove, onExport }) => {
  const [chartType, setChartType] = useState('bar');
  const [showPercentages, setShowPercentages] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('total');
  const [sortBy, setSortBy] = useState('total');

  // Karşılaştırma verilerini hazırla
  const comparisonData = useMemo(() => {
    if (calculations.length === 0) return null;

    const categories = Object.keys(calculations[0].breakdown);
    
    // Bar chart için veri
    const barData = categories.map(category => {
      const dataPoint = { category: getCategoryDisplayName(category) };
      calculations.forEach((calc, index) => {
        dataPoint[`calc_${index}`] = calc.breakdown[category] || 0;
        dataPoint[`calc_${index}_name`] = calc.title || `Hesaplama ${index + 1}`;
      });
      return dataPoint;
    });

    // Radar chart için veri
    const radarData = categories.map(category => {
      const dataPoint = { category: getCategoryDisplayName(category) };
      calculations.forEach((calc, index) => {
        dataPoint[`calc_${index}`] = calc.breakdown[category] || 0;
      });
      return dataPoint;
    });

    // Line chart için veri (zaman bazlı)
    const lineData = calculations
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((calc, index) => ({
        index: index + 1,
        date: new Date(calc.timestamp).toLocaleDateString('tr-TR'),
        total: calc.total,
        perRoomNight: calc.perRoomNight,
        title: calc.title || `Hesaplama ${index + 1}`
      }));

    // Pie chart için toplam dağılım
    const pieData = calculations.map((calc, index) => ({
      name: calc.title || `Hesaplama ${index + 1}`,
      value: calc.total,
      color: COLORS[index % COLORS.length]
    }));

    return { barData, radarData, lineData, pieData };
  }, [calculations]);

  // İstatistikleri hesapla
  const statistics = useMemo(() => {
    if (calculations.length === 0) return null;

    const totals = calculations.map(calc => calc.total);
    const perRoomNights = calculations.map(calc => calc.perRoomNight);

    const stats = {
      total: {
        max: Math.max(...totals),
        min: Math.min(...totals),
        avg: totals.reduce((sum, val) => sum + val, 0) / totals.length,
        range: Math.max(...totals) - Math.min(...totals)
      },
      perRoomNight: {
        max: Math.max(...perRoomNights),
        min: Math.min(...perRoomNights),
        avg: perRoomNights.reduce((sum, val) => sum + val, 0) / perRoomNights.length,
        range: Math.max(...perRoomNights) - Math.min(...perRoomNights)
      }
    };

    // En yüksek ve en düşük emisyonlu hesaplamaları bul
    const maxIndex = totals.indexOf(stats.total.max);
    const minIndex = totals.indexOf(stats.total.min);

    stats.highest = calculations[maxIndex];
    stats.lowest = calculations[minIndex];

    return stats;
  }, [calculations]);

  // Sıralanmış hesaplamalar
  const sortedCalculations = useMemo(() => {
    const sorted = [...calculations];
    switch (sortBy) {
      case 'total':
        return sorted.sort((a, b) => b.total - a.total);
      case 'perRoomNight':
        return sorted.sort((a, b) => b.perRoomNight - a.perRoomNight);
      case 'date':
        return sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      case 'title':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      default:
        return sorted;
    }
  }, [calculations, sortBy]);

  const handleExport = () => {
    const calculationIds = calculations.map(calc => calc.id);
    exportComparisonToCSV(calculations, 'karbon-karsilastirma.csv');
  };

  const getCategoryDisplayName = (key) => {
    const names = {
      accommodation: 'Konaklama',
      energy: 'Enerji',
      water: 'Su',
      food: 'Yemek & İçecek',
      laundry: 'Çamaşır',
      waste: 'Atık',
      transportation: 'Ulaşım',
      facilities: 'Tesis Hizmetleri'
    };
    return names[key] || key;
  };

  const formatValue = (value) => {
    return Math.round(value * 100) / 100;
  };

  const getComparisonIcon = (current, reference) => {
    if (current > reference) return <TrendingUpIcon color="error" />;
    if (current < reference) return <TrendingDownIcon color="success" />;
    return <RemoveIcon color="action" />;
  };

  if (calculations.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="h6">Karşılaştırma için hesaplama seçin</Typography>
        <Typography variant="body2">
          Geçmiş hesaplamalardan karşılaştırmak istediğiniz hesaplamaları seçerek buraya ekleyin.
          En fazla 5 hesaplama karşılaştırabilirsiniz.
        </Typography>
      </Alert>
    );
  }

  if (calculations.length === 1) {
    return (
      <Alert severity="warning">
        <Typography variant="h6">Karşılaştırma için en az 2 hesaplama gerekli</Typography>
        <Typography variant="body2">
          Karşılaştırma yapabilmek için en az bir hesaplama daha ekleyin.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Kontrol Paneli */}
      <StyledCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ⚖️ Karşılaştırma Kontrolleri
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Grafik Tipi</InputLabel>
                <Select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  label="Grafik Tipi"
                >
                  <MenuItem value="bar">
                    <Box display="flex" alignItems="center" gap={1}>
                      <BarChartIcon fontSize="small" />
                      Sütun Grafik
                    </Box>
                  </MenuItem>
                  <MenuItem value="line">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LineChartIcon fontSize="small" />
                      Çizgi Grafik
                    </Box>
                  </MenuItem>
                  <MenuItem value="radar">
                    <Box display="flex" alignItems="center" gap={1}>
                      <BarChartIcon fontSize="small" />
                      Radar Grafik
                    </Box>
                  </MenuItem>
                  <MenuItem value="pie">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PieChartIcon fontSize="small" />
                      Pasta Grafik
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sıralama</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sıralama"
                >
                  <MenuItem value="total">Toplam Emisyon</MenuItem>
                  <MenuItem value="perRoomNight">Oda-Gece Başına</MenuItem>
                  <MenuItem value="date">Tarih</MenuItem>
                  <MenuItem value="title">Başlık</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPercentages}
                    onChange={(e) => setShowPercentages(e.target.checked)}
                  />
                }
                label="Yüzde Göster"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={handleExport}
                fullWidth
              >
                📊 CSV İndir
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* İstatistik Kartları */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent textAlign="center">
                <Typography variant="h4" color="error.main">
                  {formatValue(statistics.total.max)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  En Yüksek Emisyon (kg CO₂e)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {statistics.highest.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent textAlign="center">
                <Typography variant="h4" color="success.main">
                  {formatValue(statistics.total.min)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  En Düşük Emisyon (kg CO₂e)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {statistics.lowest.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent textAlign="center">
                <Typography variant="h4" color="primary.main">
                  {formatValue(statistics.total.avg)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ortalama Emisyon (kg CO₂e)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent textAlign="center">
                <Typography variant="h4" color="warning.main">
                  {formatValue(statistics.total.range)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Fark (kg CO₂e)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Grafik Görselleştirme */}
      <StyledCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 Karşılaştırma Grafiği
          </Typography>

          <Box height={400}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'bar' && comparisonData && (
                <BarChart data={comparisonData.barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  {calculations.map((calc, index) => (
                    <Bar
                      key={`calc_${index}`}
                      dataKey={`calc_${index}`}
                      name={calc.title || `Hesaplama ${index + 1}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </BarChart>
              )}

              {chartType === 'line' && comparisonData && (
                <LineChart data={comparisonData.lineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#8884d8"
                    strokeWidth={2}
                    name="Toplam Emisyon"
                  />
                  <Line
                    type="monotone"
                    dataKey="perRoomNight"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    name="Oda-Gece Başına"
                  />
                </LineChart>
              )}

              {chartType === 'radar' && comparisonData && (
                <RadarChart data={comparisonData.radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis />
                  {calculations.map((calc, index) => (
                    <Radar
                      key={`calc_${index}`}
                      name={calc.title || `Hesaplama ${index + 1}`}
                      dataKey={`calc_${index}`}
                      stroke={COLORS[index % COLORS.length]}
                      fill={COLORS[index % COLORS.length]}
                      fillOpacity={0.6}
                    />
                  ))}
                  <Legend />
                </RadarChart>
              )}

              {chartType === 'pie' && comparisonData && (
                <RechartsPieChart>
                  <Pie
                    data={comparisonData.pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {comparisonData.pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </RechartsPieChart>
              )}
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Detaylı Karşılaştırma Tablosu */}
      <StyledCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 Detaylı Karşılaştırma
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {sortedCalculations.map((calc, index) => {
              const isHighest = statistics && calc.total === statistics.total.max;
              const isLowest = statistics && calc.total === statistics.total.min;
              
              return (
                <Grid item xs={12} md={6} lg={4} key={calc.id}>
                  <ComparisonCard isHighest={isHighest} isLowest={isLowest}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h6" noWrap>
                          {calc.title || `Hesaplama ${index + 1}`}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => onRemove(calc.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        {isHighest && <Chip label="En Yüksek" color="error" size="small" />}
                        {isLowest && <Chip label="En Düşük" color="success" size="small" />}
                        <Typography variant="caption" color="textSecondary">
                          {new Date(calc.timestamp).toLocaleDateString('tr-TR')}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Toplam Emisyon:</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="h6" color="primary">
                            {formatValue(calc.total)} kg
                          </Typography>
                          {statistics && getComparisonIcon(calc.total, statistics.total.avg)}
                        </Box>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">Oda-Gece:</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="h6" color="success.main">
                            {formatValue(calc.perRoomNight)} kg
                          </Typography>
                          {statistics && getComparisonIcon(calc.perRoomNight, statistics.perRoomNight.avg)}
                        </Box>
                      </Box>
                    </CardContent>
                  </ComparisonCard>
                </Grid>
              );
            })}
          </Grid>

          {/* Kategori Bazında Detaylı Tablo */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kategori</TableCell>
                  {calculations.map((calc, index) => (
                    <TableCell key={calc.id} align="right">
                      {calc.title || `Hesaplama ${index + 1}`}
                    </TableCell>
                  ))}
                  <TableCell align="right">Ortalama</TableCell>
                  <TableCell align="right">Fark</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(calculations[0].breakdown).map((category) => {
                  const values = calculations.map(calc => calc.breakdown[category] || 0);
                  const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
                  const max = Math.max(...values);
                  const min = Math.min(...values);
                  const range = max - min;

                  return (
                    <TableRow key={category}>
                      <TableCell>{getCategoryDisplayName(category)}</TableCell>
                      {values.map((value, index) => (
                        <TableCell key={index} align="right">
                          {formatValue(value)} kg
                          {showPercentages && calculations[index].total > 0 && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              ({((value / calculations[index].total) * 100).toFixed(1)}%)
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <strong>{formatValue(avg)} kg</strong>
                      </TableCell>
                      <TableCell align="right">
                        {formatValue(range)} kg
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Toplam satırı */}
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOPLAM</TableCell>
                  {calculations.map((calc) => (
                    <TableCell key={calc.id} align="right" sx={{ fontWeight: 'bold' }}>
                      {formatValue(calc.total)} kg
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {statistics && formatValue(statistics.total.avg)} kg
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {statistics && formatValue(statistics.total.range)} kg
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default TourismComparisonView;
